import { Platform } from "@playcharge/common/models/backend/auth/platform";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { MatFormField, MatLabel, MatPrefix } from "@angular/material/form-field";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { DateTime } from "luxon";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { OfferStatisticsStore } from "../../../stores/offers/offes-statistics.store";
import { OfferStatisticPreviewViewModel } from "@playcharge/common/models/backend/offers/backoffice/offer-statistic-preview-view-model";
import { EMPTY, switchMap } from "rxjs";
import { BillingType } from "@playcharge/common/models/backend/offers/billing-type";
import { FlagToStringPipe } from "../../../pipes/flagToString.pipe";

@Component({
    selector: "app-offer-statistics",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatDatepickerModule,
        MatPaginator,
        MatFormField,
        MatLabel,
        MatPrefix,
        FlagToStringPipe,
    ],
    templateUrl: "./offer-statistics.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferStatisticsComponent {
    private _paginator = viewChild(MatPaginator);

    private _offerStatisticStore = inject(OfferStatisticsStore);

    public form = new FormGroup({
        startDate: new FormControl<DateTime>(null!, {
            nonNullable: true,
        }),
        endDate: new FormControl<DateTime>(null!, { nonNullable: true }),
    });

    public dataSource = new MatTableDataSource<OfferStatisticPreviewViewModel>();
    public displayedColumns = signal([
        "title",
        "platform",
        "billingType",
        "price",
        "earned",
        "revenue",
        "earningsPerClick",
    ]);

    public totalRevenue = computed<number>(() =>
        this._offerStatisticStore
            .entities()
            .reduce((accumulator, stat) => accumulator + stat.revenue, 0),
    );

    public totalEarned = computed<number>(() =>
        this._offerStatisticStore
            .entities()
            .reduce((accumulator, stat) => accumulator + stat.usersEarned, 0),
    );

    public BillingType = BillingType;
    public Platform = Platform;

    constructor() {
        this.initForm();

        effect(() => this.paginatorChangeEffectFn());
    }

    private initForm(): void {
        this.form.valueChanges
            .pipe(
                takeUntilDestroyed(),
                switchMap((value) => {
                    if (value.startDate && value.endDate && value.startDate <= value.endDate) {
                        return this._offerStatisticStore.getStatistics(
                            value.startDate.startOf("day"),
                            value.endDate.startOf("day"),
                        );
                    }

                    return EMPTY;
                }),
            )
            .subscribe((statistics) => {
                this.dataSource.data = statistics;
            });

        this.form.patchValue({
            startDate: DateTime.utc().minus({ days: 1 }),
            endDate: DateTime.utc(),
        });
    }

    private paginatorChangeEffectFn(): void {
        const paginator = this._paginator();

        if (paginator) {
            this.dataSource.paginator = paginator;
        }
    }
}
